import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    useTheme,
    Grow,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

/**
 * StatusConfirmDialog - Custom confirmation modal for status changes
 * Matches the app theme with smooth animations
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close handler
 * @param {function} onConfirm - Confirm handler
 * @param {string} title - Dialog title
 * @param {string} message - Dialog message
 * @param {string} itemName - Name of item being modified (for context)
 * @param {boolean} newStatus - New status value (for visual indication)
 * @param {boolean} loading - Loading state for confirm button
 */
const StatusConfirmDialog = ({
    open = false,
    onClose,
    onConfirm,
    title = "Confirm Action",
    message = "Are you sure?",
    itemName = "",
    newStatus = true,
    statusLabel = null,
    loading = false,
}) => {
    const theme = useTheme();

    const handleConfirm = async () => {
        await onConfirm();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            TransitionComponent={Grow}
            transitionDuration={{
                enter: 400,
                exit: 300,
            }}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: `0 20px 60px rgba(0, 0, 0, 0.15)`,
                },
            }}
            BackdropProps={{
                sx: {
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    backdropFilter: "blur(4px)",
                },
            }}
        >
            {/* Header with icon and title */}
            <DialogTitle
                sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    p: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        background: newStatus
                            ? `linear-gradient(135deg, ${theme.palette.success.main}20 0%, ${theme.palette.success.light}20 100%)`
                            : `linear-gradient(135deg, ${theme.palette.warning.main}20 0%, ${theme.palette.warning.light}20 100%)`,
                    }}
                >
                    {newStatus ? (
                        <CheckCircleIcon
                            sx={{
                                color: theme.palette.success.main,
                                fontSize: "1.5rem",
                            }}
                        />
                    ) : (
                        <CancelIcon
                            sx={{
                                color: theme.palette.warning.main,
                                fontSize: "1.5rem",
                            }}
                        />
                    )}
                </Box>
                <Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            letterSpacing: 0.2,
                        }}
                    >
                        {title}
                    </Typography>
                </Box>
            </DialogTitle>

            {/* Content */}
            <DialogContent sx={{ p: 3 }}>
                <Box sx={{ my: 2 }}>
                    <Typography
                        variant="body1"
                        sx={{
                            color: theme.palette.text.primary,
                            mb: 1.5,
                            lineHeight: 1.6,
                        }}
                    >
                        {message}
                    </Typography>

                    {itemName && (
                        <Box
                            sx={{
                                p: 2,
                                backgroundColor: theme.palette.action.hover,
                                borderRadius: 1,
                                borderLeft: `4px solid ${theme.palette.primary.main}`,
                                mt: 2,
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    fontWeight: 500,
                                    mb: 0.5,
                                }}
                            >
                                Record Data
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.text.primary,
                                    fontWeight: 600,
                                    fontSize: "0.95rem",
                                }}
                            >
                                {itemName}
                            </Typography>
                        </Box>
                    )}

                    {/* Status indicator */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: 2,
                            p: 1.5,
                            backgroundColor: newStatus
                                ? theme.palette.success.lighter || `${theme.palette.success.main}15`
                                : theme.palette.warning.lighter || `${theme.palette.warning.main}15`,
                            borderRadius: 1,
                        }}
                    >
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                backgroundColor: newStatus
                                    ? theme.palette.success.main
                                    : theme.palette.warning.main,
                            }}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                color: newStatus
                                    ? theme.palette.success.dark
                                    : theme.palette.warning.dark,
                                fontWeight: 600,
                            }}
                        >
                            Status will be: <strong>{statusLabel ?? (newStatus ? "Available" : "Unavailable")}</strong>
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            {/* Actions */}
            <DialogActions
                sx={{
                    p: 3,
                    backgroundColor: theme.palette.background.default,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    gap: 1,
                }}
            >
                <Button
                    onClick={onClose}
                    variant="outlined"
                    disabled={loading}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderColor: theme.palette.divider,
                        color: theme.palette.text.primary,
                        py: 1,
                        px: 2.5,
                        "&:hover": {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: `${theme.palette.primary.main}10`,
                        },
                        transition: "all 0.2s ease",
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={loading}
                    sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        py: 1,
                        px: 2.5,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        color: "#ffffff",
                        letterSpacing: 0.5,
                        boxShadow: `0 4px 15px rgba(102, 126, 234, 0.3)`,
                        transition: "all 0.3s ease",
                        "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: `0 8px 25px rgba(102, 126, 234, 0.4)`,
                            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                        },
                        "&:active": {
                            transform: "translateY(0px)",
                        },
                        "&:disabled": {
                            background: theme.palette.action.disabledBackground,
                            color: theme.palette.action.disabled,
                            cursor: "not-allowed",
                        },
                    }}
                >
                    {loading ? "Processing..." : "Confirm"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default StatusConfirmDialog;
